import { create } from "zustand";
import { api } from "@/lib/api";

const LEGACY_KEY = "streamthumb-storage-v2";

/**
 * Backend-synced store.
 * Local state mirrors the server; mutators call the API, then update local state on success.
 */
export const useStore = create((set, get) => ({
  // State
  teams: [],
  matches: [],
  leagues: [],
  brands: [],
  activeBrandId: null, // null means "use default brand"
  generatedCount: 0,
  loaded: false,
  loading: false,

  // ============ BOOTSTRAP ============
  hydrate: async () => {
    set({ loading: true });
    try {
      const [teamsRes, leaguesRes, matchesRes, brandsRes] = await Promise.all([
        api.get("/teams"),
        api.get("/leagues"),
        api.get("/matches"),
        api.get("/brands"),
      ]);
      const savedActiveBrandId = localStorage.getItem("streamthumb-active-brand") || null;
      set({
        teams: teamsRes.data || [],
        leagues: leaguesRes.data || [],
        matches: matchesRes.data || [],
        brands: brandsRes.data || [],
        activeBrandId: savedActiveBrandId,
        loaded: true,
        loading: false,
      });
    } catch (err) {
      set({ loading: false });
      throw err;
    }
  },

  reset: () => set({ teams: [], leagues: [], matches: [], brands: [], activeBrandId: null, loaded: false }),

  // ============ LEGACY MIGRATION ============
  migrateLegacyIfNeeded: async () => {
    const raw = localStorage.getItem(LEGACY_KEY);
    if (!raw) return { migrated: false };

    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      localStorage.removeItem(LEGACY_KEY);
      return { migrated: false };
    }

    const state = parsed?.state || {};
    const teams = Array.isArray(state.teams) ? state.teams : [];
    const leagues = Array.isArray(state.leagues) ? state.leagues : [];
    const matches = Array.isArray(state.matches) ? state.matches : [];

    if (teams.length === 0 && leagues.length === 0 && matches.length === 0) {
      localStorage.removeItem(LEGACY_KEY);
      return { migrated: false };
    }

    // Only migrate if backend is empty
    try {
      const { data } = await api.get("/data/empty");
      if (!data.empty) {
        // Keep legacy around but don't migrate to avoid duplicates
        return { migrated: false, reason: "backend-not-empty" };
      }
      await api.post("/data/migrate", { teams, leagues, matches });
      localStorage.removeItem(LEGACY_KEY);
      return { migrated: true, counts: { teams: teams.length, leagues: leagues.length, matches: matches.length } };
    } catch (err) {
      return { migrated: false, error: err };
    }
  },

  // ============ LEAGUES ============
  addLeague: async (leagueData) => {
    const { data } = await api.post("/leagues", leagueData);
    set((state) => ({ leagues: [...state.leagues, data] }));
    return data;
  },

  updateLeague: async (leagueId, leagueData) => {
    // Strip id from payload to avoid accidental overwrite
    const payload = { ...leagueData };
    delete payload.id;
    const { data } = await api.put(`/leagues/${leagueId}`, payload);
    set((state) => ({
      leagues: state.leagues.map((l) => (l.id === leagueId ? data : l)),
    }));
    return data;
  },

  deleteLeague: async (leagueId) => {
    await api.delete(`/leagues/${leagueId}`);
    set((state) => ({
      leagues: state.leagues.filter((l) => l.id !== leagueId),
    }));
  },

  // ============ TEAMS ============
  addTeam: async (teamData) => {
    const { data } = await api.post("/teams", teamData);
    set((state) => ({ teams: [...state.teams, data] }));
    return data;
  },

  updateTeam: async (teamId, teamData) => {
    const payload = { ...teamData };
    delete payload.id;
    const { data } = await api.put(`/teams/${teamId}`, payload);
    set((state) => ({
      teams: state.teams.map((t) => (t.id === teamId ? data : t)),
    }));
    return data;
  },

  importTeams: async (rows) => {
    const { data } = await api.post("/teams/import", rows);
    if (data?.created?.length) {
      set((state) => ({ teams: [...state.teams, ...data.created] }));
    }
    return data; // { created, errors }
  },

  deleteTeam: async (teamId) => {
    await api.delete(`/teams/${teamId}`);
    set((state) => ({
      teams: state.teams.filter((t) => t.id !== teamId),
      leagues: state.leagues.map((l) => ({
        ...l,
        teamIds: (l.teamIds || []).filter((id) => id !== teamId),
      })),
    }));
  },

  bulkDeleteTeams: async (teamIds) => {
    if (!teamIds || teamIds.length === 0) return { deleted: 0 };
    const { data } = await api.post("/teams/bulk-delete", teamIds);
    const idSet = new Set(teamIds);
    set((state) => ({
      teams: state.teams.filter((t) => !idSet.has(t.id)),
      leagues: state.leagues.map((l) => ({
        ...l,
        teamIds: (l.teamIds || []).filter((id) => !idSet.has(id)),
      })),
    }));
    return data;
  },

  // ============ MATCHES ============
  addMatch: async (matchData) => {
    const { data } = await api.post("/matches", matchData);
    set((state) => ({ matches: [...state.matches, data] }));
    return data;
  },

  addMatches: async (matchesData) => {
    const { data } = await api.post("/matches/bulk", matchesData);
    set((state) => ({ matches: [...state.matches, ...data] }));
    return data;
  },

  updateMatch: async (matchId, matchData) => {
    const payload = { ...matchData };
    delete payload.id;
    const { data } = await api.put(`/matches/${matchId}`, payload);
    set((state) => ({
      matches: state.matches.map((m) => (m.id === matchId ? data : m)),
    }));
    return data;
  },

  deleteMatch: async (matchId) => {
    await api.delete(`/matches/${matchId}`);
    set((state) => ({
      matches: state.matches.filter((m) => m.id !== matchId),
    }));
  },

  deleteMatches: async (matchIds) => {
    await api.post("/matches/bulk-delete", matchIds);
    set((state) => ({
      matches: state.matches.filter((m) => !matchIds.includes(m.id)),
    }));
  },

  markMatchGenerated: async (matchId) => {
    const current = get().matches.find((m) => m.id === matchId);
    if (!current || current.thumbnailGenerated) return;
    const payload = { ...current, thumbnailGenerated: true };
    delete payload.id;
    const { data } = await api.put(`/matches/${matchId}`, payload);
    set((state) => ({
      matches: state.matches.map((m) => (m.id === matchId ? data : m)),
    }));
  },

  incrementGeneratedCount: () => {
    set((state) => ({ generatedCount: state.generatedCount + 1 }));
  },

  // ============ BRANDS ============
  addBrand: async (brandData) => {
    const { data } = await api.post("/brands", brandData);
    set((state) => ({ brands: [...state.brands, data] }));
    return data;
  },

  updateBrand: async (brandId, brandData) => {
    const payload = { ...brandData };
    delete payload.id;
    delete payload.isDefault;
    const { data } = await api.put(`/brands/${brandId}`, payload);
    set((state) => ({
      brands: state.brands.map((b) => (b.id === brandId ? data : b)),
    }));
    return data;
  },

  deleteBrand: async (brandId) => {
    await api.delete(`/brands/${brandId}`);
    // refresh brand list to pick up any default-promotion server-side
    const { data } = await api.get("/brands");
    set((state) => ({
      brands: data || [],
      activeBrandId: state.activeBrandId === brandId ? null : state.activeBrandId,
    }));
    if (useStore.getState().activeBrandId === null) {
      localStorage.removeItem("streamthumb-active-brand");
    }
  },

  setDefaultBrand: async (brandId) => {
    const { data } = await api.post(`/brands/${brandId}/default`);
    set((state) => ({
      brands: state.brands.map((b) => ({ ...b, isDefault: b.id === brandId })),
    }));
    return data;
  },

  setActiveBrand: (brandId) => {
    if (brandId) {
      localStorage.setItem("streamthumb-active-brand", brandId);
    } else {
      localStorage.removeItem("streamthumb-active-brand");
    }
    set({ activeBrandId: brandId });
  },

  // ============ BULK + FIXTURES ============
  clearAllData: async () => {
    const { teams, leagues, matches } = get();
    await Promise.all([
      ...matches.map((m) => api.delete(`/matches/${m.id}`).catch(() => null)),
      ...leagues.map((l) => api.delete(`/leagues/${l.id}`).catch(() => null)),
      ...teams.map((t) => api.delete(`/teams/${t.id}`).catch(() => null)),
    ]);
    set({ teams: [], leagues: [], matches: [], generatedCount: 0 });
  },

  /**
   * Generates round-robin fixtures server-side and returns the count.
   * Ensures the league's teamIds have been persisted before calling.
   */
  generateFixtures: async (leagueId) => {
    const { data } = await api.post(`/leagues/${leagueId}/fixtures`);
    set((state) => ({ matches: [...state.matches, ...data] }));
    return data.length;
  },

  /**
   * Re-fetch matches from the server (call after bulk operations to drop any
   * stale auto-generated rows that the server replaced).
   */
  reloadMatches: async () => {
    const { data } = await api.get("/matches");
    set({ matches: data || [] });
  },
}));
